import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Send, Search, RefreshCw, MessageSquare, Mail, Filter, Clock,
  AlertTriangle, ShieldAlert, Eye, History, Plus, Trash2, Save,
  ScrollText, UserX, Layers, FileText, Sparkles, CheckCircle2,
  X, ChevronRight, Hash, Activity
} from 'lucide-react';
import api from '@/lib/api';

// ─── Helpers ───────────────────────────────────────────────────────────────
const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(Number(v || 0));

const fmtDateBR = (iso) => {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR');
  } catch {
    return iso;
  }
};

const renderTemplate = (tpl, ctx) => {
  let out = tpl || '';
  Object.entries(ctx || {}).forEach(([k, v]) => {
    out = out.split(`{{${k}}}`).join(v == null ? '' : String(v));
  });
  return out;
};

const ETAPA_COLORS = {
  1: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  2: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  3: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  4: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
};

const EVENT_BADGE = {
  envio: { label: 'Envio', cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200', icon: Send },
  criacao: { label: 'Criação', cls: 'bg-slate-50 text-slate-700 ring-1 ring-slate-200', icon: Plus },
  leitura: { label: 'Leitura', cls: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200', icon: Eye },
  erro: { label: 'Erro', cls: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200', icon: AlertTriangle },
  pagamento: { label: 'Pagamento', cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200', icon: CheckCircle2 },
  renegociacao: { label: 'Renegociação', cls: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200', icon: RefreshCw },
  cancelamento: { label: 'Cancelamento', cls: 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-300', icon: X },
  anotacao: { label: 'Anotação', cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', icon: ScrollText },
};

const CANAL_OPTIONS = [
  { v: 'whatsapp', l: 'WhatsApp', icon: MessageSquare },
  { v: 'email', l: 'E-mail', icon: Mail },
  { v: 'ambos', l: 'Ambos', icon: Send },
];

// ─── Página ────────────────────────────────────────────────────────────────
export default function DnaPayCobranca() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'devedores');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    items: [], total: 0, total_valor: 0, por_etapa: {}, ex_alunos: 0,
    regua: { etapas: [] }, variaveis: [],
  });
  const [templates, setTemplates] = useState([]);
  const [regua, setRegua] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    q: '', etapa: 'all', canal: 'all', status_envio: 'all',
    turma_id: 'all', aluno_id: 'all',
    incluir_ex_alunos: true, apenas_ex_alunos: false, dias_min: 1,
  });

  // Seleção
  const [selected, setSelected] = useState(new Set());
  const [showHistorico, setShowHistorico] = useState(false);
  const [historicoData, setHistoricoData] = useState(null);

  // Envio manual (WhatsApp/Email) — substitui mock
  const [showManualSend, setShowManualSend] = useState(false);
  const [manualSendCanal, setManualSendCanal] = useState('whatsapp');
  const [manualSendItems, setManualSendItems] = useState([]);

  // Ex-aluno manual
  const [showExAluno, setShowExAluno] = useState(false);

  // Importação Excel
  const [showImport, setShowImport] = useState(false);

  // Régua automática (fila WhatsApp + PDF)
  const [showRegua, setShowRegua] = useState(false);

  // ─── Loaders ──────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        dias_min: filters.dias_min || 0,
        incluir_ex_alunos: filters.incluir_ex_alunos,
        apenas_ex_alunos: filters.apenas_ex_alunos,
      };
      if (filters.q) params.q = filters.q;
      if (filters.etapa !== 'all') params.etapa = Number(filters.etapa);
      if (filters.canal !== 'all') params.canal = filters.canal;
      if (filters.status_envio !== 'all') params.status_envio = filters.status_envio;
      if (filters.turma_id !== 'all') params.turma_id = filters.turma_id;
      if (filters.aluno_id !== 'all') params.aluno_id = filters.aluno_id;
      const { data: r } = await api.get('/dnapay/cobranca', { params });
      setData(r);
      setSelected(new Set());
    } catch (e) {
      toast.error('Erro ao carregar cobranças', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadTemplates = useCallback(async () => {
    try {
      const { data: r } = await api.get('/dnapay/cobranca/templates');
      setTemplates(r.items || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadRegua = useCallback(async () => {
    try {
      const { data: r } = await api.get('/dnapay/cobranca/regua');
      setRegua(r);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadTemplates(); loadRegua(); }, [loadTemplates, loadRegua]);

  // ─── Histórico ────────────────────────────────────────────────────────
  const openHistorico = async (tituloId) => {
    try {
      const { data: r } = await api.get(`/dnapay/cobranca/historico/${tituloId}`);
      setHistoricoData(r);
      setShowHistorico(true);
    } catch (e) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((i) => i.titulo_id)));
  };

  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const openManualSend = (canal, items) => {
    if (!items || items.length === 0) return;
    setManualSendCanal(canal);
    setManualSendItems(items);
    setShowManualSend(true);
  };

  // ─── KPIs ─────────────────────────────────────────────────────────────
  const kpis = useMemo(() => ([
    { label: 'Devedores', value: data.devedores_unicos ?? data.total, icon: AlertTriangle, accent: 'text-amber-600 bg-amber-50' },
    { label: 'Valor atualizado', value: fmtBRL(data.valor_atualizado_total ?? data.total_valor), icon: ScrollText, accent: 'text-rose-600 bg-rose-50' },
    { label: 'Ex-alunos', value: data.ex_alunos_unicos ?? data.ex_alunos, icon: UserX, accent: 'text-violet-600 bg-violet-50' },
    { label: 'Selecionados', value: selected.size, icon: CheckCircle2, accent: 'text-emerald-600 bg-emerald-50' },
  ]), [data, selected]);

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="dnapay-cobranca-page">
      {/* Header */}
      <div className="border-b border-zinc-200/70 bg-white">
        <div className="px-8 py-6 max-w-[1600px] mx-auto flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium tracking-wider text-zinc-500 uppercase">
              <ShieldAlert className="h-3.5 w-3.5" /> DNA Pay V2 · Financeiro
            </div>
            <h1 className="mt-1.5 text-3xl font-semibold text-zinc-900 tracking-tight">
              Cobrança Extrajudicial
            </h1>
            <p className="mt-1 text-sm text-zinc-500 max-w-2xl">
              Régua administrativa de cobrança via WhatsApp/E-mail, com templates dinâmicos,
              timeline auditável e suporte a ex-alunos.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const r = await api.get('/dnapay/cobranca/importar-excel/template', { responseType: 'blob' });
                  const url = window.URL.createObjectURL(r.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'modelo_cobranca_extrajudicial.xlsx';
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Modelo baixado');
                } catch { toast.error('Falha ao baixar modelo'); }
              }}
              data-testid="cobranca-template-btn"
              className="rounded-full border-zinc-300"
            >
              <FileText className="h-4 w-4 mr-2" />
              Baixar Modelo
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowImport(true)}
              data-testid="cobranca-import-btn"
              className="rounded-full border-zinc-300"
            >
              <Layers className="h-4 w-4 mr-2" />
              Importar Excel
            </Button>
            <Button
              onClick={() => setShowRegua(true)}
              data-testid="cobranca-regua-auto-btn"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Activity className="h-4 w-4 mr-2" />
              Régua Automática
            </Button>
            <Button
              onClick={() => setShowExAluno(true)}
              data-testid="cobranca-add-exaluno-btn"
              className="rounded-full bg-zinc-900 hover:bg-zinc-800"
            >
              <UserX className="h-4 w-4 mr-2" />
              Incluir ex-aluno manualmente
            </Button>
            <Button
              variant="outline"
              onClick={loadList}
              disabled={loading}
              data-testid="cobranca-refresh-btn"
              className="rounded-full border-zinc-300"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-8 py-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((k) => (
            <div key={k.label} className="bg-white rounded-2xl border border-zinc-200/70 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-medium tracking-wider text-zinc-500 uppercase">{k.label}</div>
                  <div className="mt-2 text-2xl font-semibold tabular-nums text-zinc-900">{k.value}</div>
                </div>
                <div className={`p-2 rounded-lg ${k.accent}`}>
                  <k.icon className="h-4 w-4" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-zinc-200/70 rounded-full p-1 mb-6">
            <TabsTrigger value="devedores" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-devedores">
              Devedores
            </TabsTrigger>
            <TabsTrigger value="inadimplentes" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-inadimplentes">
              Inadimplentes (por título)
            </TabsTrigger>
            <TabsTrigger value="templates" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-templates">
              Templates
            </TabsTrigger>
            <TabsTrigger value="regua" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-regua">
              Régua
            </TabsTrigger>
            <TabsTrigger value="config" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-config">
              Configurações
            </TabsTrigger>
            <TabsTrigger value="importacoes" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-importacoes">
              Importações
            </TabsTrigger>
          </TabsList>

          {/* TAB DEVEDORES (consolidado por responsável) */}
          <TabsContent value="devedores">
            <DevedoresTab />
          </TabsContent>

          {/* TAB INADIMPLENTES */}
          <TabsContent value="inadimplentes" className="space-y-6">
            <FiltrosCard filters={filters} setFilters={setFilters} regua={data.regua} items={data.items} />

            <Card className="border-zinc-200/70 rounded-2xl shadow-none overflow-hidden">
              <CardHeader className="border-b border-zinc-100 bg-white py-4 flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base font-semibold">Títulos elegíveis</CardTitle>
                  {selected.size > 0 && (
                    <Badge variant="secondary" className="bg-zinc-100 text-zinc-700">
                      {selected.size} selecionado(s)
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {filters.turma_id !== 'all' && data.items.length > 0 && (
                    <>
                      <Button
                        onClick={() => openManualSend('whatsapp', data.items)}
                        data-testid="cobranca-turma-whatsapp-btn"
                        className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                        title={`Enviar WhatsApp para todos os ${data.items.length} títulos da turma`}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        WhatsApp para turma ({data.items.length})
                      </Button>
                      <Button
                        onClick={() => openManualSend('email', data.items)}
                        data-testid="cobranca-turma-email-btn"
                        variant="outline"
                        className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        title={`Enviar E-mail para todos os ${data.items.length} títulos da turma`}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        E-mail para turma
                      </Button>
                      <Separator orientation="vertical" className="h-6 mx-1" />
                    </>
                  )}
                  <Button
                    onClick={() => openManualSend('whatsapp', data.items.filter((i) => selected.has(i.titulo_id)))}
                    disabled={selected.size === 0}
                    data-testid="cobranca-bulk-whatsapp-btn"
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    WhatsApp selecionados
                  </Button>
                  <Button
                    onClick={() => openManualSend('email', data.items.filter((i) => selected.has(i.titulo_id)))}
                    disabled={selected.size === 0}
                    data-testid="cobranca-bulk-email-btn"
                    variant="outline"
                    className="rounded-full border-zinc-300"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    E-mail selecionados
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[60vh]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={data.items.length > 0 && selected.size === data.items.length}
                            onCheckedChange={toggleAll}
                            data-testid="cobranca-select-all"
                          />
                        </TableHead>
                        <TableHead>Aluno / Responsável</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-center">Atraso</TableHead>
                        <TableHead>Etapa</TableHead>
                        <TableHead>Último envio</TableHead>
                        <TableHead className="w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-12 text-zinc-400">
                            Nenhum título encontrado com os filtros atuais.
                          </TableCell>
                        </TableRow>
                      )}
                      {data.items.map((it) => (
                        <TableRow
                          key={it.titulo_id}
                          className="hover:bg-zinc-50/60"
                          data-testid={`cobranca-row-${it.titulo_id}`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selected.has(it.titulo_id)}
                              onCheckedChange={() => toggleOne(it.titulo_id)}
                              data-testid={`cobranca-check-${it.titulo_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="font-medium text-zinc-900">{it.aluno_nome || '—'}</div>
                                <div className="text-xs text-zinc-500">
                                  {it.responsavel_nome || it.turma_nome || '—'}
                                </div>
                              </div>
                              {it.is_ex_aluno && (
                                <Badge className="bg-violet-50 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50" data-testid={`badge-ex-${it.titulo_id}`}>
                                  Ex-aluno
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-zinc-700">
                            {it.categoria || '—'}
                            <div className="text-xs text-zinc-400">{it.competencia}</div>
                          </TableCell>
                          <TableCell className="text-sm tabular-nums">{fmtDateBR(it.vencimento)}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{fmtBRL(it.valor_atualizado)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="tabular-nums border-zinc-300">
                              {it.dias_atraso}d
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${ETAPA_COLORS[it.etapa_id] || 'bg-zinc-50 text-zinc-700 ring-1 ring-zinc-200'}`}>
                              {it.etapa_nome || `Etapa ${it.etapa_id || '?'}`}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {it.ultimo_envio_at ? (
                              <div>
                                <div className="text-zinc-700">{fmtDT(it.ultimo_envio_at)}</div>
                                <div className="text-zinc-400">{it.ultimo_canal}</div>
                              </div>
                            ) : (
                              <span className="text-zinc-400">Sem envio</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openManualSend('whatsapp', [it])}
                                data-testid={`cobranca-wa-${it.titulo_id}`}
                                className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                                title="Abrir WhatsApp"
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openManualSend('email', [it])}
                                data-testid={`cobranca-email-${it.titulo_id}`}
                                className="text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                                title="Abrir E-mail"
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openHistorico(it.titulo_id)}
                                data-testid={`cobranca-historico-${it.titulo_id}`}
                                title="Timeline"
                              >
                                <History className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB TEMPLATES */}
          <TabsContent value="templates">
            <TemplatesTab
              templates={templates}
              variaveis={data.variaveis}
              onReload={loadTemplates}
            />
          </TabsContent>

          {/* TAB RÉGUA */}
          <TabsContent value="regua">
            <ReguaTab regua={regua} onReload={loadRegua} />
          </TabsContent>

          {/* TAB CONFIGURAÇÕES */}
          <TabsContent value="config">
            <ConfiguracoesTab />
          </TabsContent>

          {/* TAB IMPORTAÇÕES */}
          <TabsContent value="importacoes">
            <ImportacoesTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Envio manual em fila (WhatsApp/E-mail) — substitui mock */}
      <ManualSendQueueDialog
        open={showManualSend}
        canal={manualSendCanal}
        items={manualSendItems}
        templates={templates}
        variaveis={data.variaveis}
        onClose={() => setShowManualSend(false)}
        onAnyAction={loadList}
      />

      {/* Cadastro manual de ex-aluno */}
      <ExAlunoManualDialog
        open={showExAluno}
        onClose={() => setShowExAluno(false)}
        onCreated={() => { setShowExAluno(false); loadList(); }}
      />

      {/* Importação via Excel */}
      <ImportarExcelDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => { setShowImport(false); loadList(); }}
      />

      {/* Régua Automática (fila WhatsApp + PDF) */}
      <ReguaAutomaticaDialog
        open={showRegua}
        onClose={() => setShowRegua(false)}
        onExecuted={loadList}
        templates={templates}
      />

      {/* Drawer de timeline */}
      <Sheet open={showHistorico} onOpenChange={setShowHistorico}>
        <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="cobranca-historico-sheet">
          <SheetHeader>
            <SheetTitle>Timeline de Cobrança</SheetTitle>
            <SheetDescription>
              {historicoData?.titulo?.aluno_nome} —{' '}
              {fmtBRL(historicoData?.titulo?.saldo_atualizado || historicoData?.titulo?.valor_final || historicoData?.titulo?.valor_original)}
              {' · '}venc. {fmtDateBR(historicoData?.titulo?.vencimento)}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {(historicoData?.timeline || []).length === 0 && (
              <div className="text-center text-sm text-zinc-400 py-12">Nenhum evento registrado ainda.</div>
            )}
            {(historicoData?.timeline || []).map((ev) => {
              const meta = EVENT_BADGE[ev.event_type] || EVENT_BADGE.envio;
              const Icon = meta.icon;
              return (
                <div key={ev.id} className="relative pl-8 pb-6 border-l border-zinc-200 last:border-transparent" data-testid={`timeline-event-${ev.id}`}>
                  <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full ${meta.cls} flex items-center justify-center`}>
                    <Icon className="h-3 w-3" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-zinc-800">
                      {meta.label}{ev.canal ? ` · ${ev.canal}` : ''}
                      {ev.etapa_nome && <span className="text-zinc-400 font-normal"> · {ev.etapa_nome}</span>}
                    </div>
                    <span className="text-xs text-zinc-400">{fmtDT(ev.created_at)}</span>
                  </div>
                  {ev.assunto && <div className="text-xs font-medium text-zinc-600 mt-1">{ev.assunto}</div>}
                  {ev.corpo && (
                    <div className="mt-2 bg-zinc-50 rounded-lg p-3 text-xs whitespace-pre-wrap text-zinc-700 border border-zinc-100">
                      {ev.corpo}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-400 tabular-nums">
                    {ev.usuario_email && <span>por {ev.usuario_email}</span>}
                    {ev.ip && <span>IP {ev.ip}</span>}
                    {ev.hash_mensagem && <span className="font-mono"><Hash className="h-3 w-3 inline -mt-0.5" /> {ev.hash_mensagem}</span>}
                    {ev.provider && <span>· {ev.provider}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Filtros ───────────────────────────────────────────────────────────────
function FiltrosCard({ filters, setFilters, regua, items }) {
  // Opções únicas derivadas do dataset atual
  const turmas = useMemo(() => {
    const map = new Map();
    (items || []).forEach((i) => {
      if (i.turma_id && !map.has(i.turma_id)) map.set(i.turma_id, i.turma_nome || i.turma_id);
    });
    return Array.from(map.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'pt-BR'));
  }, [items]);
  const alunos = useMemo(() => {
    const map = new Map();
    (items || []).forEach((i) => {
      const sid = i.aluno_id;
      if (sid && !map.has(sid)) map.set(sid, i.aluno_nome || sid);
    });
    // Se turma selecionada, filtrar alunos daquela turma
    if (filters.turma_id !== 'all') {
      const filtered = new Map();
      (items || []).forEach((i) => {
        if (i.turma_id === filters.turma_id && i.aluno_id && !filtered.has(i.aluno_id)) {
          filtered.set(i.aluno_id, i.aluno_nome || i.aluno_id);
        }
      });
      return Array.from(filtered.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'pt-BR'));
    }
    return Array.from(map.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'pt-BR'));
  }, [items, filters.turma_id]);

  return (
    <Card className="border-zinc-200/70 rounded-2xl shadow-none">
      <CardContent className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Buscar</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Aluno, responsável, categoria..."
                className="pl-9 rounded-full border-zinc-300"
                data-testid="cobranca-filter-q"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Turma</Label>
            <Select value={filters.turma_id} onValueChange={(v) => setFilters((f) => ({ ...f, turma_id: v, aluno_id: 'all' }))}>
              <SelectTrigger className="rounded-full border-zinc-300" data-testid="cobranca-filter-turma">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as turmas</SelectItem>
                {turmas.map(([id, nome]) => (
                  <SelectItem key={id} value={id}>{nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Aluno</Label>
            <Select value={filters.aluno_id} onValueChange={(v) => setFilters((f) => ({ ...f, aluno_id: v }))}>
              <SelectTrigger className="rounded-full border-zinc-300" data-testid="cobranca-filter-aluno">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os alunos</SelectItem>
                {alunos.map(([id, nome]) => (
                  <SelectItem key={id} value={id}>{nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Etapa</Label>
            <Select value={String(filters.etapa)} onValueChange={(v) => setFilters((f) => ({ ...f, etapa: v }))}>
              <SelectTrigger className="rounded-full border-zinc-300" data-testid="cobranca-filter-etapa">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {(regua?.etapas || []).map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>{e.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Canal último envio</Label>
            <Select value={filters.canal} onValueChange={(v) => setFilters((f) => ({ ...f, canal: v }))}>
              <SelectTrigger className="rounded-full border-zinc-300" data-testid="cobranca-filter-canal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="ambos">Ambos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Dias mínimos</Label>
            <Input
              type="number"
              min={0}
              value={filters.dias_min}
              onChange={(e) => setFilters((f) => ({ ...f, dias_min: Number(e.target.value || 0) }))}
              className="rounded-full border-zinc-300 tabular-nums"
              data-testid="cobranca-filter-dias"
            />
          </div>
          <div className="flex gap-3 items-center md:col-span-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={filters.incluir_ex_alunos}
                onCheckedChange={(v) => setFilters((f) => ({ ...f, incluir_ex_alunos: v, apenas_ex_alunos: v ? f.apenas_ex_alunos : false }))}
                data-testid="cobranca-filter-incluir-ex"
              />
              <Label className="text-xs text-zinc-600">Ex-alunos</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={filters.apenas_ex_alunos}
                onCheckedChange={(v) => setFilters((f) => ({ ...f, apenas_ex_alunos: v, incluir_ex_alunos: v ? true : f.incluir_ex_alunos }))}
                data-testid="cobranca-filter-apenas-ex"
              />
              <Label className="text-xs text-zinc-600">Só ex</Label>
            </div>
            {(filters.turma_id !== 'all' || filters.aluno_id !== 'all' || filters.etapa !== 'all' || filters.canal !== 'all' || filters.q) && (
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, q: '', etapa: 'all', canal: 'all', turma_id: 'all', aluno_id: 'all' }))}
                className="text-xs text-indigo-600 hover:text-indigo-800 underline ml-auto"
                data-testid="cobranca-filter-clear"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Helpers de envio manual ───────────────────────────────────────────────
function onlyDigits(s) { return String(s || '').replace(/\D/g, ''); }

function buildWhatsappLink(phone, msg) {
  const num = onlyDigits(phone);
  const full = num.length >= 10 ? (num.startsWith('55') ? num : `55${num}`) : num;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg || '')}`;
}

function buildMailto(to, subject, body) {
  const qs = [];
  if (subject) qs.push(`subject=${encodeURIComponent(subject)}`);
  if (body) qs.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${to || ''}${qs.length ? '?' + qs.join('&') : ''}`;
}

async function fetchRenderCtx(tituloId) {
  try {
    const { data } = await api.post('/dnapay/cobranca/render', {
      titulo_id: tituloId, corpo: '', assunto: '',
    });
    return data.contexto || {};
  } catch {
    return {};
  }
}

async function logManualAction(action, item, payload) {
  try {
    await api.post('/dnapay/cobranca/log-manual-action', {
      titulo_id: item.titulo_id,
      action,
      canal: payload.canal,
      template_id: payload.template_id || null,
      template_nome: payload.template_nome || null,
      assunto: payload.assunto || null,
      corpo: payload.corpo || null,
      destino: payload.destino || null,
    });
  } catch (e) {
    console.error('Falha ao registrar ação manual:', e);
  }
}

// ─── Modal de Envio Manual em Fila ─────────────────────────────────────────
function ManualSendQueueDialog({ open, canal, items, templates, variaveis, onClose, onAnyAction }) {
  const [idx, setIdx] = useState(0);
  const [templateId, setTemplateId] = useState('');
  const [customCorpo, setCustomCorpo] = useState('');
  const [customAssunto, setCustomAssunto] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [ctx, setCtx] = useState({});

  const filteredTemplates = useMemo(
    () => templates.filter((t) => t.canal === canal),
    [templates, canal]
  );
  const selectedTpl = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);
  const current = items[idx];

  useEffect(() => {
    if (!open) {
      setIdx(0); setTemplateId(''); setCustomCorpo(''); setCustomAssunto(''); setUseCustom(false);
    }
  }, [open]);

  useEffect(() => {
    if (!current) return;
    let cancel = false;
    fetchRenderCtx(current.titulo_id).then((c) => { if (!cancel) setCtx(c); });
    return () => { cancel = true; };
  }, [current]);

  const corpoFinal = useMemo(() => {
    const tpl = useCustom ? customCorpo : (selectedTpl?.corpo || '');
    return renderTemplate(tpl, ctx);
  }, [useCustom, customCorpo, selectedTpl, ctx]);

  const assuntoFinal = useMemo(() => {
    const tpl = useCustom ? customAssunto : (selectedTpl?.assunto || '');
    return renderTemplate(tpl, ctx) || `Cobrança - ${current?.aluno_nome || ''}`;
  }, [useCustom, customAssunto, selectedTpl, ctx, current]);

  if (!current) return null;

  const destinoWA = current.responsavel_whatsapp || ctx.responsavel_whatsapp || '';
  const destinoEmail = current.responsavel_email || ctx.responsavel_email || '';
  const destino = canal === 'whatsapp' ? destinoWA : destinoEmail;

  const insertVar = (v) => {
    if (useCustom) setCustomCorpo((c) => `${c}{{${v}}}`);
  };

  const handleOpenWhatsApp = async () => {
    if (!corpoFinal.trim()) { toast.error('Mensagem vazia'); return; }
    if (!destinoWA) { toast.error('Responsável sem WhatsApp cadastrado'); return; }
    const url = buildWhatsappLink(destinoWA, corpoFinal);
    window.open(url, '_blank', 'noopener,noreferrer');
    await logManualAction('whatsapp_manual_opened', current, {
      canal: 'whatsapp',
      template_id: useCustom ? null : templateId || null,
      template_nome: selectedTpl?.nome,
      corpo: corpoFinal,
      destino: destinoWA,
    });
    toast.success('WhatsApp aberto', { description: 'Registrado em WHATSAPP_MANUAL_OPENED' });
    onAnyAction && onAnyAction();
  };

  const handleOpenEmail = async () => {
    if (!corpoFinal.trim()) { toast.error('Mensagem vazia'); return; }
    if (!destinoEmail) { toast.error('Responsável sem e-mail cadastrado'); return; }
    const url = buildMailto(destinoEmail, assuntoFinal, corpoFinal);
    window.location.href = url;
    await logManualAction('email_manual_opened', current, {
      canal: 'email',
      template_id: useCustom ? null : templateId || null,
      template_nome: selectedTpl?.nome,
      assunto: assuntoFinal,
      corpo: corpoFinal,
      destino: destinoEmail,
    });
    toast.success('Cliente de e-mail aberto', { description: 'Registrado em EMAIL_MANUAL_OPENED' });
    onAnyAction && onAnyAction();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        canal === 'email' && assuntoFinal
          ? `${assuntoFinal}\n\n${corpoFinal}`
          : corpoFinal
      );
      await logManualAction('message_copied', current, {
        canal,
        template_id: useCustom ? null : templateId || null,
        template_nome: selectedTpl?.nome,
        assunto: canal === 'email' ? assuntoFinal : null,
        corpo: corpoFinal,
        destino,
      });
      toast.success('Mensagem copiada', { description: 'Registrado em MESSAGE_COPIED' });
      onAnyAction && onAnyAction();
    } catch {
      toast.error('Falha ao copiar');
    }
  };

  const next = () => {
    if (idx < items.length - 1) setIdx(idx + 1);
    else { toast.info('Fila concluída'); onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="manual-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {canal === 'whatsapp' ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            Envio manual · {canal === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
          </DialogTitle>
          <DialogDescription>
            Item {idx + 1} de {items.length} · <span className="font-medium text-zinc-800">{current.aluno_nome}</span>
            {' '}— {fmtBRL(current.valor_atualizado)}
            <div className="mt-1 text-xs">
              Destino: <span className="font-mono text-zinc-700">{destino || <span className="text-rose-600">não cadastrado</span>}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Template</Label>
            <Select
              value={templateId || 'none'}
              onValueChange={(v) => { setTemplateId(v === 'none' ? '' : v); setUseCustom(false); }}
              disabled={useCustom}
            >
              <SelectTrigger className="rounded-lg" data-testid="manual-send-template"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— sem template —</SelectItem>
                {filteredTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={useCustom} onCheckedChange={setUseCustom} data-testid="manual-send-custom-switch" />
            <Label className="text-sm">Editar mensagem livremente</Label>
          </div>

          {useCustom && (
            <>
              {canal === 'email' && (
                <div>
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Assunto</Label>
                  <Input value={customAssunto} onChange={(e) => setCustomAssunto(e.target.value)} className="rounded-lg" />
                </div>
              )}
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Mensagem (com variáveis)</Label>
                <Textarea
                  value={customCorpo}
                  onChange={(e) => setCustomCorpo(e.target.value)}
                  rows={6}
                  className="rounded-lg font-mono text-sm"
                  data-testid="manual-send-custom-corpo"
                  placeholder="Olá {{responsavel_nome}}, a mensalidade de {{aluno_nome}}..."
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(variaveis || []).map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(v)}
                    className="px-2 py-0.5 text-[11px] rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-600">
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <Separator className="my-4" />

        <div>
          <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Preview
          </div>
          <div className="bg-zinc-50 border border-zinc-200/70 rounded-xl p-4 min-h-[120px] text-sm whitespace-pre-wrap text-zinc-700" data-testid="manual-send-preview">
            {canal === 'email' && assuntoFinal && (
              <div className="font-medium text-zinc-900 mb-2 pb-2 border-b border-zinc-200/70">{assuntoFinal}</div>
            )}
            {corpoFinal || <span className="text-zinc-400">Selecione um template ou edite uma mensagem...</span>}
          </div>
        </div>

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleCopy} disabled={!corpoFinal.trim()} data-testid="manual-send-copy-btn">
            <ScrollText className="h-4 w-4 mr-2" /> Copiar mensagem
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            {canal === 'whatsapp' ? (
              <Button
                onClick={handleOpenWhatsApp}
                disabled={!corpoFinal.trim() || !destinoWA}
                data-testid="manual-send-open-wa-btn"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <MessageSquare className="h-4 w-4 mr-2" /> Abrir WhatsApp
              </Button>
            ) : (
              <Button
                onClick={handleOpenEmail}
                disabled={!corpoFinal.trim() || !destinoEmail}
                data-testid="manual-send-open-email-btn"
                className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Mail className="h-4 w-4 mr-2" /> Abrir no e-mail
              </Button>
            )}
            {items.length > 1 && (
              <Button variant="outline" onClick={next} className="rounded-full" data-testid="manual-send-next-btn">
                {idx < items.length - 1 ? 'Próximo' : 'Finalizar'} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal de Cadastro Manual de Ex-Aluno ──────────────────────────────────
function ExAlunoManualDialog({ open, onClose, onCreated }) {
  const empty = {
    aluno_nome: '', responsavel_nome: '', responsavel_cpf: '',
    responsavel_whatsapp: '', responsavel_email: '',
    competencia: '', vencimento: '', valor_original: '', valor_atualizado: '',
    categoria: 'Mensalidade', observacoes: '',
    link_pagamento: '', linha_digitavel: '',
    data_saida: '', motivo_saida: '',
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.aluno_nome.trim() || !form.responsavel_nome.trim()) {
      toast.error('Aluno e responsável são obrigatórios'); return;
    }
    if (!form.vencimento || !form.valor_original) {
      toast.error('Vencimento e valor original são obrigatórios'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor_original: Number(form.valor_original),
        valor_atualizado: form.valor_atualizado ? Number(form.valor_atualizado) : null,
      };
      await api.post('/dnapay/cobranca/ex-aluno-manual', payload);
      toast.success('Ex-aluno incluído', { description: 'Registrado como EX_ALUNO_MANUAL_CREATED' });
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error('Já existe', { description: detail });
      } else {
        toast.error('Erro ao salvar', { description: detail || e.message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="exaluno-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-4 w-4" /> Incluir ex-aluno manualmente
          </DialogTitle>
          <DialogDescription>
            Cadastre devedores que <strong>não estão</strong> mais matriculados. O título entra na cobrança extrajudicial automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Nome do aluno *</Label>
            <Input value={form.aluno_nome} onChange={(e) => upd('aluno_nome', e.target.value)} data-testid="exaluno-aluno-nome" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Nome do responsável financeiro *</Label>
            <Input value={form.responsavel_nome} onChange={(e) => upd('responsavel_nome', e.target.value)} data-testid="exaluno-resp-nome" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">CPF/CNPJ</Label>
            <Input value={form.responsavel_cpf} onChange={(e) => upd('responsavel_cpf', e.target.value)} data-testid="exaluno-cpf" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">WhatsApp</Label>
            <Input value={form.responsavel_whatsapp} onChange={(e) => upd('responsavel_whatsapp', e.target.value)} placeholder="(DDD) 9 9999-9999" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">E-mail</Label>
            <Input type="email" value={form.responsavel_email} onChange={(e) => upd('responsavel_email', e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Competência</Label>
            <Input value={form.competencia} onChange={(e) => upd('competencia', e.target.value)} placeholder="2024-11 ou 11/2024" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Vencimento *</Label>
            <Input type="date" value={form.vencimento} onChange={(e) => upd('vencimento', e.target.value)} data-testid="exaluno-vencimento" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Valor original *</Label>
            <Input type="number" step="0.01" value={form.valor_original} onChange={(e) => upd('valor_original', e.target.value)} data-testid="exaluno-valor" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Valor atualizado</Label>
            <Input type="number" step="0.01" value={form.valor_atualizado} onChange={(e) => upd('valor_atualizado', e.target.value)} placeholder="(opcional)" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Categoria</Label>
            <Input value={form.categoria} onChange={(e) => upd('categoria', e.target.value)} placeholder="Mensalidade, Material, etc." />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Data de saída</Label>
            <Input type="date" value={form.data_saida} onChange={(e) => upd('data_saida', e.target.value)} data-testid="exaluno-data-saida" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Motivo da saída</Label>
            <Select value={form.motivo_saida || 'none'} onValueChange={(v) => upd('motivo_saida', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="exaluno-motivo-saida"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— não informado —</SelectItem>
                <SelectItem value="transferencia">Transferência</SelectItem>
                <SelectItem value="desligamento">Desligamento</SelectItem>
                <SelectItem value="cancelamento">Cancelamento</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Link de pagamento</Label>
            <Input value={form.link_pagamento} onChange={(e) => upd('link_pagamento', e.target.value)} placeholder="https://..." />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Linha digitável</Label>
            <Input value={form.linha_digitavel} onChange={(e) => upd('linha_digitavel', e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Observações</Label>
            <Textarea rows={3} value={form.observacoes} onChange={(e) => upd('observacoes', e.target.value)} />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="exaluno-salvar-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvando...' : 'Cadastrar ex-aluno'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tab Templates ─────────────────────────────────────────────────────────
function TemplatesTab({ templates, variaveis, onReload }) {
  const [editing, setEditing] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const openNew = () => {
    setEditing({ nome: '', canal: 'whatsapp', assunto: '', corpo: '', ativo: true });
    setShowEdit(true);
  };

  const openEdit = (t) => {
    setEditing({ ...t });
    setShowEdit(true);
  };

  const save = async () => {
    if (!editing.nome.trim() || !editing.corpo.trim()) {
      toast.error('Preencha nome e corpo'); return;
    }
    try {
      if (editing.id) {
        await api.put(`/dnapay/cobranca/templates/${editing.id}`, editing);
        toast.success('Template atualizado');
      } else {
        await api.post('/dnapay/cobranca/templates', editing);
        toast.success('Template criado');
      }
      setShowEdit(false);
      onReload();
    } catch (e) {
      toast.error('Erro ao salvar', { description: e?.response?.data?.detail });
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir template "${t.nome}"?`)) return;
    try {
      await api.delete(`/dnapay/cobranca/templates/${t.id}`);
      toast.success('Template excluído');
      onReload();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  return (
    <Card className="border-zinc-200/70 rounded-2xl shadow-none">
      <CardHeader className="border-b border-zinc-100 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base font-semibold">Templates de Mensagem</CardTitle>
          <p className="text-xs text-zinc-500 mt-1">Use variáveis dinâmicas — substituídas em tempo real no envio.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="templates-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo template
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80">
              <TableHead>Nome</TableHead>
              <TableHead>Canal</TableHead>
              <TableHead>Assunto</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-zinc-400">
                  Nenhum template cadastrado.
                </TableCell>
              </TableRow>
            )}
            {templates.map((t) => (
              <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                <TableCell className="font-medium">{t.nome}</TableCell>
                <TableCell>
                  <Badge className={t.canal === 'whatsapp' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'}>
                    {t.canal}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-zinc-600">{t.assunto || '—'}</TableCell>
                <TableCell className="text-center">
                  {t.ativo ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> : <X className="h-4 w-4 text-zinc-400 inline" />}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}>Editar</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-rose-600 hover:text-rose-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Editar' : 'Novo'} template</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Nome</Label>
                  <Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} data-testid="template-input-nome" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Canal</Label>
                  <Select value={editing.canal} onValueChange={(v) => setEditing({ ...editing, canal: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editing.canal === 'email' && (
                <div>
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Assunto</Label>
                  <Input value={editing.assunto || ''} onChange={(e) => setEditing({ ...editing, assunto: e.target.value })} />
                </div>
              )}
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Corpo da mensagem</Label>
                <Textarea
                  value={editing.corpo}
                  onChange={(e) => setEditing({ ...editing, corpo: e.target.value })}
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="template-input-corpo"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Variáveis disponíveis</Label>
                <div className="flex flex-wrap gap-1.5">
                  {(variaveis || []).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setEditing((e) => ({ ...e, corpo: `${e.corpo}{{${v}}}` }))}
                      className="px-2 py-0.5 text-[11px] rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-600"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.ativo} onCheckedChange={(v) => setEditing({ ...editing, ativo: v })} />
                <Label className="text-sm">Ativo</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEdit(false)}>Cancelar</Button>
            <Button onClick={save} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="template-salvar-btn">
              <Save className="h-4 w-4 mr-2" /> Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Tab Régua ─────────────────────────────────────────────────────────────
function ReguaTab({ regua, onReload }) {
  const [draft, setDraft] = useState(null);

  useEffect(() => { if (regua) setDraft(JSON.parse(JSON.stringify(regua))); }, [regua]);

  if (!draft) return <div className="text-zinc-400 text-sm p-6">Carregando régua...</div>;

  const updateEtapa = (idx, key, val) => {
    const cp = JSON.parse(JSON.stringify(draft));
    cp.etapas[idx][key] = key === 'dias_min' || key === 'dias_max' || key === 'id' ? Number(val) : val;
    setDraft(cp);
  };

  const addEtapa = () => {
    setDraft({
      ...draft,
      etapas: [
        ...draft.etapas,
        { id: (draft.etapas.length + 1), nome: `Etapa ${draft.etapas.length + 1}`, dias_min: 0, dias_max: 999, canal: 'whatsapp' }
      ],
    });
  };

  const removeEtapa = (idx) => {
    const cp = JSON.parse(JSON.stringify(draft));
    cp.etapas.splice(idx, 1);
    setDraft(cp);
  };

  const save = async () => {
    try {
      await api.post('/dnapay/cobranca/regua', draft);
      toast.success('Régua salva');
      onReload();
    } catch (e) {
      toast.error('Erro ao salvar régua', { description: e?.response?.data?.detail });
    }
  };

  return (
    <Card className="border-zinc-200/70 rounded-2xl shadow-none">
      <CardHeader className="border-b border-zinc-100 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base font-semibold">Régua de Cobrança</CardTitle>
          <p className="text-xs text-zinc-500 mt-1">Etapas administrativas por dias de atraso · Canal sugerido por etapa.</p>
        </div>
        <Button onClick={save} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="regua-salvar-btn">
          <Save className="h-4 w-4 mr-2" /> Salvar
        </Button>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Horário de envio</Label>
            <Input
              type="time"
              value={draft.horario_envio || '09:00'}
              onChange={(e) => setDraft({ ...draft, horario_envio: e.target.value })}
              className="rounded-lg"
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Switch checked={draft.pausa_ao_pagar} onCheckedChange={(v) => setDraft({ ...draft, pausa_ao_pagar: v })} />
            <Label className="text-sm">Pausar ao pagar</Label>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Switch checked={draft.ignora_renegociado} onCheckedChange={(v) => setDraft({ ...draft, ignora_renegociado: v })} />
            <Label className="text-sm">Ignorar renegociado/conciliado</Label>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs font-medium uppercase tracking-wider text-zinc-500">Etapas</Label>
            <Button variant="outline" size="sm" onClick={addEtapa} className="rounded-full" data-testid="regua-add-etapa">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Etapa
            </Button>
          </div>
          <div className="space-y-2">
            {draft.etapas.map((e, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-zinc-50/50 border border-zinc-200/70 rounded-xl p-3" data-testid={`regua-etapa-${idx}`}>
                <Input className="col-span-1 text-center rounded-lg" type="number" value={e.id} onChange={(ev) => updateEtapa(idx, 'id', ev.target.value)} />
                <Input className="col-span-4 rounded-lg" value={e.nome} onChange={(ev) => updateEtapa(idx, 'nome', ev.target.value)} placeholder="Nome da etapa" />
                <Input className="col-span-2 rounded-lg tabular-nums" type="number" value={e.dias_min} onChange={(ev) => updateEtapa(idx, 'dias_min', ev.target.value)} placeholder="Dias mín" />
                <Input className="col-span-2 rounded-lg tabular-nums" type="number" value={e.dias_max} onChange={(ev) => updateEtapa(idx, 'dias_max', ev.target.value)} placeholder="Dias máx" />
                <Select value={e.canal} onValueChange={(v) => updateEtapa(idx, 'canal', v)}>
                  <SelectTrigger className="col-span-2 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="ambos">Ambos</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => removeEtapa(idx)} className="col-span-1 text-rose-600 hover:text-rose-700">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Importação Excel ──────────────────────────────────────────────────────
function ImportarExcelDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!open) { setFile(null); setPreview(null); setReport(null); setLoading(false); }
  }, [open]);

  const accept = (f) => {
    if (!f) return;
    const name = (f.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      toast.error('Arquivo deve ser .xlsx ou .xls'); return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('Arquivo maior que 20MB'); return;
    }
    setFile(f); setPreview(null); setReport(null);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    accept(e.dataTransfer.files?.[0]);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/dnapay/cobranca/importar-excel?dry_run=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
      toast.success(`Preview: ${data.totais.validos} válidos, ${data.totais.duplicados} dup, ${data.totais.erros} erros`);
    } catch (e) {
      toast.error('Falha no preview', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  const runImport = async () => {
    if (!file || !preview) return;
    if (preview.totais.validos === 0) { toast.error('Nada para importar'); return; }
    if (!window.confirm(`Confirma importar ${preview.totais.validos} cobrança(s)?`)) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/dnapay/cobranca/importar-excel?dry_run=false', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(data);
      toast.success(`${data.totais.importados} cobrança(s) importada(s)`);
      onImported && onImported();
    } catch (e) {
      toast.error('Falha na importação', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  const data = report || preview;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4" /> Importar Cobranças Extrajudiciais — Excel
          </DialogTitle>
          <DialogDescription>
            Suporta .xlsx/.xls até 20MB. Aceita aliases das colunas (aluno, responsavel, cpf, whatsapp, email, competencia, vencimento, valor, etc.).
          </DialogDescription>
        </DialogHeader>

        {!data && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`mt-2 border-2 border-dashed rounded-2xl p-10 text-center transition ${dragOver ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-300 bg-white'}`}
            data-testid="import-dropzone"
          >
            <Layers className="h-10 w-10 mx-auto text-zinc-400" />
            <div className="mt-3 text-sm text-zinc-700">
              {file ? <span className="font-medium">{file.name} <span className="text-zinc-400">({(file.size/1024).toFixed(1)} KB)</span></span> : 'Arraste o arquivo aqui ou'}
            </div>
            <div className="mt-3">
              <label className="inline-block px-4 py-2 text-sm rounded-full bg-zinc-900 text-white cursor-pointer hover:bg-zinc-800">
                Selecionar arquivo
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => accept(e.target.files?.[0])} data-testid="import-file-input" />
              </label>
            </div>
            {file && !preview && (
              <Button onClick={runPreview} disabled={loading} className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-preview-btn">
                {loading ? 'Validando...' : 'Validar arquivo (preview)'}
              </Button>
            )}
          </div>
        )}

        {data && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
            <KpiPill label="Linhas" value={data.totais.linhas} color="zinc" />
            <KpiPill label="Válidos" value={data.totais.validos} color="emerald" />
            <KpiPill label="Duplicados" value={data.totais.duplicados} color="amber" />
            <KpiPill label="Com erro" value={data.totais.erros} color="rose" />
            <KpiPill label="Importados" value={data.totais.importados} color={report ? 'emerald' : 'zinc'} />
          </div>
        )}

        {data && (
          <div className="mt-4 max-h-[40vh] overflow-auto border border-zinc-200/70 rounded-xl" data-testid="import-preview-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80 sticky top-0">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Aluno</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.preview || []).map((row) => {
                  const cls = row.status === 'ok'
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                    : row.status === 'duplicado'
                    ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                    : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200';
                  return (
                    <TableRow key={row.linha}>
                      <TableCell className="text-xs text-zinc-400 tabular-nums">{row.linha}</TableCell>
                      <TableCell className="text-sm">{row.aluno_nome || '—'}</TableCell>
                      <TableCell className="text-sm">{row.responsavel_nome || '—'}</TableCell>
                      <TableCell className="text-xs font-mono">{row.responsavel_cpf || '—'}</TableCell>
                      <TableCell className="text-xs tabular-nums">{row.vencimento || '—'}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtBRL(row.valor_original || 0)}</TableCell>
                      <TableCell className="text-xs">{row.responsavel_whatsapp || '—'}</TableCell>
                      <TableCell className="text-xs">{row.responsavel_email || '—'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`} title={(row.erros || []).join(', ')}>
                          {row.status}
                          {row.erros?.length ? ` · ${row.erros[0]}` : ''}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Fechar</Button>
          {preview && !report && (
            <Button
              onClick={runImport}
              disabled={loading || preview.totais.validos === 0}
              className="rounded-full bg-zinc-900 hover:bg-zinc-800"
              data-testid="import-confirm-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Importando...' : `Importar ${preview.totais.validos}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiPill({ label, value, color }) {
  const colors = {
    zinc: 'bg-zinc-50 text-zinc-700 ring-zinc-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    rose: 'bg-rose-50 text-rose-700 ring-rose-200',
  };
  return (
    <div className={`rounded-xl px-3 py-2 ring-1 ${colors[color] || colors.zinc}`}>
      <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

// ─── Régua Automática ──────────────────────────────────────────────────────
function ReguaAutomaticaDialog({ open, onClose, onExecuted, templates }) {
  const [config, setConfig] = useState({
    apenas_importados: true, apenas_ex_alunos: true,
    dias_min: 1, template_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) { setResult(null); setIdx(0); setLoading(false); }
  }, [open]);

  const waTemplates = useMemo(() => templates.filter((t) => t.canal === 'whatsapp'), [templates]);

  const exec = async () => {
    setLoading(true);
    try {
      const payload = { ...config, template_id: config.template_id || null };
      const { data } = await api.post('/dnapay/cobranca/regua-automatica', payload);
      setResult(data);
      setIdx(0);
      toast.success(`${data.totais.gerados} mensagem(ns) preparada(s)`, {
        description: data.totais.sem_telefone ? `${data.totais.sem_telefone} sem telefone` : 'Todas com WhatsApp',
      });
      onExecuted && onExecuted();
    } catch (e) {
      toast.error('Falha ao executar régua', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  const openNext = () => {
    const item = result?.items?.[idx];
    if (!item) return;
    window.open(item.link_whatsapp, '_blank', 'noopener,noreferrer');
    if (idx < result.items.length - 1) setIdx(idx + 1);
    else toast.info('Fila concluída — baixe o PDF para evidência');
  };

  const openAll = () => {
    if (!result?.items?.length) return;
    if (!window.confirm(`Abrir ${result.items.length} abas do WhatsApp? Seu navegador pode bloquear pop-ups.`)) return;
    result.items.forEach((it, i) => {
      setTimeout(() => window.open(it.link_whatsapp, '_blank', 'noopener,noreferrer'), i * 300);
    });
  };

  const downloadPdf = async () => {
    if (!result?.batch_id) return;
    try {
      const r = await api.get(`/dnapay/cobranca/regua-automatica/${result.batch_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `regua_evidencia_${result.batch_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório PDF baixado');
    } catch { toast.error('Falha ao baixar PDF'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="regua-auto-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" /> Régua Automática — Disparo WhatsApp em Lote
          </DialogTitle>
          <DialogDescription>
            Percorre todos os ex-alunos elegíveis, gera links wa.me em fila e registra evidência auditável (hash + IP + usuário + timestamp).
            Você ainda envia manualmente — cada link abre o WhatsApp pré-preenchido.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Template (WhatsApp)</Label>
              <Select value={config.template_id || 'auto'} onValueChange={(v) => setConfig({ ...config, template_id: v === 'auto' ? '' : v })}>
                <SelectTrigger className="rounded-lg" data-testid="regua-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">— automático (1º WhatsApp ativo) —</SelectItem>
                  {waTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Dias mínimos de atraso</Label>
              <Input type="number" min={1} value={config.dias_min} onChange={(e) => setConfig({ ...config, dias_min: Number(e.target.value || 1) })} className="rounded-lg" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={config.apenas_importados} onCheckedChange={(v) => setConfig({ ...config, apenas_importados: v })} data-testid="regua-apenas-importados" />
              <Label className="text-sm">Apenas ex-alunos (importados/manuais)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={config.apenas_ex_alunos} onCheckedChange={(v) => setConfig({ ...config, apenas_ex_alunos: v })} />
              <Label className="text-sm">Excluir alunos ativos</Label>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-2 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiPill label="Gerados" value={result.totais.gerados} color="emerald" />
              <KpiPill label="Sem telefone" value={result.totais.sem_telefone} color="amber" />
              <KpiPill label="Posição na fila" value={`${Math.min(idx + 1, result.items.length)} / ${result.items.length}`} color="zinc" />
              <KpiPill label="Valor total" value={fmtBRL(result.totais.total_valor)} color="rose" />
            </div>

            {result.items.length > 0 && (
              <div className="bg-zinc-50 border border-zinc-200/70 rounded-2xl p-4" data-testid="regua-current-item">
                <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-1">
                  Próximo na fila ({idx + 1}/{result.items.length})
                </div>
                <div className="text-base font-semibold text-zinc-900">{result.items[idx]?.aluno_nome}</div>
                <div className="text-xs text-zinc-500">{result.items[idx]?.responsavel_nome} · {result.items[idx]?.responsavel_whatsapp} · {fmtBRL(result.items[idx]?.valor_atualizado)}</div>
                <div className="mt-3 bg-white border border-zinc-200/70 rounded-lg p-3 text-xs whitespace-pre-wrap text-zinc-700 max-h-[140px] overflow-y-auto">
                  {result.items[idx]?.mensagem}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button onClick={openNext} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="regua-open-next-btn">
                    <MessageSquare className="h-4 w-4 mr-2" /> Abrir WhatsApp e avançar
                  </Button>
                  <Button variant="outline" onClick={openAll} className="rounded-full" data-testid="regua-open-all-btn">
                    Abrir todos ({result.items.length})
                  </Button>
                  <span className="ml-auto text-[11px] text-zinc-400 font-mono">
                    hash {result.items[idx]?.hash} · {result.items[idx]?.timestamp?.slice(0, 19).replace('T', ' ')}
                  </span>
                </div>
              </div>
            )}

            {result.sem_telefone?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <strong>{result.sem_telefone.length}</strong> ex-aluno(s) sem WhatsApp cadastrado foram excluídos da fila:
                <ul className="list-disc list-inside mt-1">
                  {result.sem_telefone.slice(0, 5).map((s) => <li key={s.titulo_id}>{s.aluno_nome} ({s.responsavel_nome})</li>)}
                  {result.sem_telefone.length > 5 && <li className="opacity-60">... e mais {result.sem_telefone.length - 5}</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Fechar</Button>
          {!result && (
            <Button onClick={exec} disabled={loading} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="regua-exec-btn">
              <Activity className="h-4 w-4 mr-2" />
              {loading ? 'Preparando...' : 'Disparar régua agora'}
            </Button>
          )}
          {result && (
            <Button onClick={downloadPdf} className="rounded-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="regua-pdf-btn">
              <FileText className="h-4 w-4 mr-2" /> Baixar evidência PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Tab DEVEDORES (consolidado por responsável) ───────────────────────────
function DevedoresTab() {
  const [data, setData] = useState({ items: [], total: 0, total_valor_atualizado: 0, settings: {} });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [diasMin, setDiasMin] = useState(1);
  const [comStatus, setComStatus] = useState('pendente');
  const [detalheKey, setDetalheKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { dias_min: diasMin, com_status: comStatus };
      if (q) params.q = q;
      const { data: r } = await api.get('/dnapay/cobranca/devedores', { params });
      setData(r);
    } catch (e) {
      toast.error('Erro ao carregar devedores', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [q, diasMin, comStatus]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (key) => {
    try {
      const r = await api.get(`/dnapay/cobranca/devedores/${encodeURIComponent(key)}/demonstrativo.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `demonstrativo_${key.replace(':', '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Demonstrativo baixado');
    } catch { toast.error('Falha ao gerar PDF'); }
  };

  const sendWhatsApp = async (it) => {
    if (!it.responsavel_whatsapp) { toast.error('Sem WhatsApp cadastrado'); return; }
    try {
      const { data: r } = await api.post(`/dnapay/cobranca/devedores/${encodeURIComponent(it.devedor_key)}/render`, {
        canal: 'whatsapp', link_pagamento: '',
      });
      const link = buildWhatsappLink(it.responsavel_whatsapp, r.corpo_renderizado);
      window.open(link, '_blank', 'noopener,noreferrer');
      // Audit
      const firstTitulo = await api.get(`/dnapay/cobranca/devedores/${encodeURIComponent(it.devedor_key)}/detalhe`);
      const t0 = firstTitulo.data.debitos[0];
      if (t0) {
        await api.post('/dnapay/cobranca/log-manual-action', {
          titulo_id: t0.titulo_id, action: 'whatsapp_manual_opened',
          canal: 'whatsapp', corpo: r.corpo_renderizado, destino: it.responsavel_whatsapp,
        });
      }
      toast.success('WhatsApp aberto');
    } catch (e) {
      toast.error('Falha', { description: e?.response?.data?.detail || e.message });
    }
  };

  const sendEmail = async (it) => {
    if (!it.responsavel_email) { toast.error('Sem e-mail cadastrado'); return; }
    try {
      const { data: r } = await api.post(`/dnapay/cobranca/devedores/${encodeURIComponent(it.devedor_key)}/render`, {
        canal: 'email', link_pagamento: '',
      });
      const url = buildMailto(it.responsavel_email, r.assunto_renderizado, r.corpo_renderizado);
      window.location.href = url;
      const firstTitulo = await api.get(`/dnapay/cobranca/devedores/${encodeURIComponent(it.devedor_key)}/detalhe`);
      const t0 = firstTitulo.data.debitos[0];
      if (t0) {
        await api.post('/dnapay/cobranca/log-manual-action', {
          titulo_id: t0.titulo_id, action: 'email_manual_opened',
          canal: 'email', assunto: r.assunto_renderizado, corpo: r.corpo_renderizado, destino: it.responsavel_email,
        });
      }
      toast.success('E-mail aberto');
    } catch (e) {
      toast.error('Falha', { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-zinc-200/70 rounded-2xl shadow-none">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs text-zinc-500 mb-1.5 block">Buscar devedor</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, CPF, aluno..." className="pl-9 rounded-full border-zinc-300" data-testid="devedores-q" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Dias mínimos atraso</Label>
              <Input type="number" min={0} value={diasMin} onChange={(e) => setDiasMin(Number(e.target.value || 0))} className="rounded-full border-zinc-300" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Status</Label>
              <Select value={comStatus} onValueChange={setComStatus}>
                <SelectTrigger className="rounded-full border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="renegociado">Acordos</SelectItem>
                  <SelectItem value="baixado_manual">Quitados</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-200/70 rounded-2xl shadow-none overflow-hidden">
        <CardHeader className="border-b border-zinc-100 py-4 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">
            Devedores ({data.total}) ·{' '}
            <span className="text-rose-700">{fmtBRL(data.total_valor_atualizado)}</span>
            <span className="text-xs text-zinc-500 font-normal ml-2">atualizado</span>
          </CardTitle>
          <div className="text-xs text-zinc-500">
            Multa {data.settings?.multa_pct || 0}% · Juros {data.settings?.juros_mes_pct || 0}% a.m.
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="max-h-[65vh]">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80">
                  <TableHead>Responsável</TableHead>
                  <TableHead>Aluno(s)</TableHead>
                  <TableHead className="text-center">Débitos</TableHead>
                  <TableHead className="text-right">Original</TableHead>
                  <TableHead className="text-right">Multa</TableHead>
                  <TableHead className="text-right">Juros</TableHead>
                  <TableHead className="text-right">Atualizado</TableHead>
                  <TableHead className="text-center">Atraso</TableHead>
                  <TableHead>Etapa</TableHead>
                  <TableHead className="text-right w-44">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={10} className="text-center py-12 text-zinc-400">Nenhum devedor encontrado.</TableCell></TableRow>
                )}
                {data.items.map((it) => (
                  <TableRow key={it.devedor_key} className="hover:bg-zinc-50/60" data-testid={`devedor-row-${it.devedor_key}`}>
                    <TableCell>
                      <div className="font-medium text-zinc-900">{it.responsavel_nome || '—'}</div>
                      <div className="text-xs text-zinc-500 font-mono">{it.responsavel_cpf || '—'}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {(it.alunos || []).slice(0, 2).join(', ')}
                      {it.alunos?.length > 2 && <span className="text-zinc-400"> +{it.alunos.length - 2}</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="tabular-nums">{it.quantidade_debitos}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{fmtBRL(it.valor_original_total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-zinc-500">{fmtBRL(it.multa_total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-zinc-500">{fmtBRL(it.juros_total)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-rose-700">{fmtBRL(it.valor_atualizado_total)}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="tabular-nums">{it.dias_atraso_max}d</Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${ETAPA_COLORS[it.etapa_id] || 'bg-zinc-50 text-zinc-700 ring-1 ring-zinc-200'}`}>
                        {it.etapa_nome || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="sm" onClick={() => sendWhatsApp(it)} className="text-emerald-700 hover:bg-emerald-50" title="WhatsApp consolidado" data-testid={`devedor-wa-${it.devedor_key}`}>
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => sendEmail(it)} className="text-blue-700 hover:bg-blue-50" title="E-mail consolidado">
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(it.devedor_key)} title="Demonstrativo PDF" data-testid={`devedor-pdf-${it.devedor_key}`}>
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDetalheKey(it.devedor_key)} data-testid={`devedor-detalhe-${it.devedor_key}`}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      <DetalheDevedorSheet
        devedorKey={detalheKey}
        open={!!detalheKey}
        onClose={() => setDetalheKey(null)}
        onChanged={load}
      />
    </div>
  );
}

// ─── Sheet: Detalhe do devedor (débitos + ações) ───────────────────────────
function DetalheDevedorSheet({ devedorKey, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showProposta, setShowProposta] = useState(false);

  const load = useCallback(async () => {
    if (!devedorKey) return;
    setLoading(true);
    try {
      const { data: r } = await api.get(`/dnapay/cobranca/devedores/${encodeURIComponent(devedorKey)}/detalhe`);
      setData(r);
    } catch (e) {
      toast.error('Erro ao carregar detalhe', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  }, [devedorKey]);

  useEffect(() => { if (open) load(); else setData(null); }, [open, load]);

  const acao = async (action, t) => {
    const labelMap = { acordo: 'marcar como ACORDO', quitar: 'marcar como QUITADO MANUAL' };
    const obs = window.prompt(`${labelMap[action]} - ${t.categoria || 'débito'} de ${fmtBRL(t.valor_original)}\n\nObservação (opcional):`, '');
    if (obs === null) return;
    try {
      await api.post(`/dnapay/cobranca/titulo/${action}`, { titulo_id: t.titulo_id, observacao: obs || null });
      toast.success(action === 'acordo' ? 'Acordo registrado' : 'Quitação registrada');
      load(); onChanged && onChanged();
    } catch (e) {
      toast.error('Falha', { description: e?.response?.data?.detail });
    }
  };

  const downloadPdf = async () => {
    if (!devedorKey) return;
    try {
      const r = await api.get(`/dnapay/cobranca/devedores/${encodeURIComponent(devedorKey)}/demonstrativo.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `demonstrativo_${devedorKey.replace(':', '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Falha ao gerar PDF'); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-3xl overflow-y-auto" data-testid="devedor-detalhe-sheet">
        <SheetHeader>
          <SheetTitle>Detalhe do Devedor</SheetTitle>
          <SheetDescription>
            {data?.responsavel_nome} {data?.responsavel_cpf && `· CPF ${data.responsavel_cpf}`}
            <div className="text-xs mt-1">
              {data?.responsavel_whatsapp && <>WhatsApp: <span className="font-mono">{data.responsavel_whatsapp}</span> · </>}
              {data?.responsavel_email && <>E-mail: <span className="font-mono">{data.responsavel_email}</span></>}
            </div>
          </SheetDescription>
        </SheetHeader>

        {loading && <div className="text-center text-zinc-400 py-12">Calculando débitos...</div>}

        {data && (
          <>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiPill label="Débitos" value={data.totais.quantidade_debitos} color="zinc" />
              <KpiPill label="Original" value={fmtBRL(data.totais.valor_original_total)} color="zinc" />
              <KpiPill label="Multa+Juros" value={fmtBRL((data.totais.multa_total || 0) + (data.totais.juros_total || 0))} color="amber" />
              <KpiPill label="Atualizado" value={fmtBRL(data.totais.valor_atualizado_total)} color="rose" />
            </div>

            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80">
                    <TableHead>Categoria</TableHead>
                    <TableHead>Comp.</TableHead>
                    <TableHead>Venc.</TableHead>
                    <TableHead className="text-right">Original</TableHead>
                    <TableHead className="text-right">Multa</TableHead>
                    <TableHead className="text-right">Juros</TableHead>
                    <TableHead className="text-right">Atual.</TableHead>
                    <TableHead className="text-center">Atraso</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.debitos || []).map((d) => {
                    const isClosed = ['baixado_manual', 'pago', 'renegociado', 'cancelado'].includes((d.status || '').toLowerCase());
                    const stCls = d.status === 'baixado_manual' || d.status === 'pago'
                      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                      : d.status === 'renegociado'
                      ? 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200'
                      : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200';
                    return (
                      <TableRow key={d.titulo_id} data-testid={`debito-row-${d.titulo_id}`}>
                        <TableCell className="text-sm">{d.categoria || '—'}</TableCell>
                        <TableCell className="text-xs">{d.competencia || '—'}</TableCell>
                        <TableCell className="text-xs tabular-nums">{fmtDateBR(d.vencimento)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{fmtBRL(d.valor_original)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-zinc-500">{fmtBRL(d.multa)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-zinc-500">{fmtBRL(d.juros)}</TableCell>
                        <TableCell className="text-xs text-right font-semibold tabular-nums text-rose-700">{fmtBRL(d.valor_atualizado)}</TableCell>
                        <TableCell className="text-xs text-center">{d.dias_atraso}d</TableCell>
                        <TableCell>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${stCls}`}>{d.status}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {!isClosed && (
                            <div className="flex items-center gap-0.5">
                              <Button variant="ghost" size="sm" onClick={() => acao('acordo', d)} title="Marcar acordo" data-testid={`debito-acordo-${d.titulo_id}`}>
                                <RefreshCw className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => acao('quitar', d)} title="Quitação manual" className="text-emerald-700" data-testid={`debito-quitar-${d.titulo_id}`}>
                                <CheckCircle2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowProposta(true)} className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="devedor-proposta-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Simular Acordo
              </Button>
              <Button variant="outline" onClick={downloadPdf} className="rounded-full" data-testid="devedor-pdf-btn">
                <FileText className="h-4 w-4 mr-2" /> Demonstrativo PDF
              </Button>
            </div>

            <PropostaAcordoDialog
              open={showProposta}
              onClose={() => setShowProposta(false)}
              devedor={data}
              onAccepted={() => { setShowProposta(false); load(); onChanged && onChanged(); }}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Tab CONFIGURAÇÕES ─────────────────────────────────────────────────────
function ConfiguracoesTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/dnapay/cobranca/configuracoes');
      setCfg(data);
    } catch { toast.error('Erro ao carregar configurações'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        multa_pct: Number(cfg.multa_pct || 0),
        juros_mes_pct: Number(cfg.juros_mes_pct || 0),
        juros_dia_pct: Number(cfg.juros_dia_pct || 0),
        aplicar_juros_apos_dias: Number(cfg.aplicar_juros_apos_dias || 0),
        correcao_pct: Number(cfg.correcao_pct || 0),
        arredondamento: Number(cfg.arredondamento || 2),
        texto_whatsapp: cfg.texto_whatsapp,
        texto_email: cfg.texto_email,
        telefone_financeiro: cfg.telefone_financeiro,
        email_financeiro: cfg.email_financeiro,
      };
      await api.put('/dnapay/cobranca/configuracoes', payload);
      toast.success('Configurações salvas');
      load();
    } catch (e) {
      toast.error('Erro ao salvar', { description: e?.response?.data?.detail });
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-zinc-400 p-6">Carregando...</div>;

  return (
    <Card className="border-zinc-200/70 rounded-2xl shadow-none">
      <CardHeader className="border-b border-zinc-100 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base font-semibold">Motor de Cobrança · Multa, Juros e Textos</CardTitle>
          <p className="text-xs text-zinc-500 mt-1">Parâmetros aplicados em todos os cálculos de cobrança extrajudicial.</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="config-save-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </CardHeader>
      <CardContent className="p-5 space-y-6">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-3">Cálculo financeiro</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Multa (%)</Label>
              <Input type="number" step="0.01" value={cfg.multa_pct} onChange={(e) => upd('multa_pct', e.target.value)} className="rounded-lg" data-testid="config-multa" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Juros ao mês (%)</Label>
              <Input type="number" step="0.01" value={cfg.juros_mes_pct} onChange={(e) => upd('juros_mes_pct', e.target.value)} className="rounded-lg" data-testid="config-juros-mes" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Juros ao dia (%) — opcional</Label>
              <Input type="number" step="0.001" value={cfg.juros_dia_pct} onChange={(e) => upd('juros_dia_pct', e.target.value)} className="rounded-lg" placeholder="(0 = usa juros/mês÷30)" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Aplicar juros após (dias)</Label>
              <Input type="number" value={cfg.aplicar_juros_apos_dias} onChange={(e) => upd('aplicar_juros_apos_dias', e.target.value)} className="rounded-lg" data-testid="config-apos-dias" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Correção (%) — opcional</Label>
              <Input type="number" step="0.01" value={cfg.correcao_pct} onChange={(e) => upd('correcao_pct', e.target.value)} className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Arredondamento (casas)</Label>
              <Input type="number" value={cfg.arredondamento} onChange={(e) => upd('arredondamento', e.target.value)} className="rounded-lg" />
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-3">Contatos do setor financeiro</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Telefone</Label>
              <Input value={cfg.telefone_financeiro || ''} onChange={(e) => upd('telefone_financeiro', e.target.value)} className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">E-mail</Label>
              <Input type="email" value={cfg.email_financeiro || ''} onChange={(e) => upd('email_financeiro', e.target.value)} className="rounded-lg" />
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-3">Texto padrão WhatsApp</div>
          <Textarea rows={5} value={cfg.texto_whatsapp || ''} onChange={(e) => upd('texto_whatsapp', e.target.value)} className="font-mono text-xs rounded-lg" data-testid="config-texto-wa" />
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 mb-3">Texto padrão E-mail</div>
          <Textarea rows={6} value={cfg.texto_email || ''} onChange={(e) => upd('texto_email', e.target.value)} className="font-mono text-xs rounded-lg" />
        </div>

        <div className="text-[11px] text-zinc-400">
          Variáveis disponíveis: {`{{responsavel_nome}}`}, {`{{aluno_nome}}`}, {`{{quantidade_debitos}}`}, {`{{valor_original_total}}`}, {`{{valor_atualizado_total}}`}, {`{{vencimento_mais_antigo}}`}, {`{{dias_atraso}}`}, {`{{detalhamento_debitos}}`}, {`{{link_pagamento}}`}, {`{{telefone_financeiro}}`}, {`{{email_financeiro}}`}, {`{{nome_escola}}`}, {`{{cnpj_escola}}`}.
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Proposta de Acordo ────────────────────────────────────────────────────
function PropostaAcordoDialog({ open, onClose, devedor, onAccepted }) {
  const [tipo, setTipo] = useState('avista');
  const [descontoPct, setDescontoPct] = useState(10);
  const [parcelas, setParcelas] = useState(3);
  const [primDias, setPrimDias] = useState(7);
  const [intervalo, setIntervalo] = useState(30);
  const [validadeDias, setValidadeDias] = useState(7);
  const [observacao, setObservacao] = useState('');
  const [proposta, setProposta] = useState(null);
  const [loading, setLoading] = useState(false);
  // V4.30 — Simulador enterprise (client-side em tempo real)
  const [entradaTipo, setEntradaTipo] = useState('none'); // none | pct | valor
  const [entradaValor, setEntradaValor] = useState(0);
  const [entradaVencimentoDias, setEntradaVencimentoDias] = useState(''); // V4.33 — vazio = usa primDias (1ª parcela)
  const [descPrincipal, setDescPrincipal] = useState(0);
  const [descMulta, setDescMulta] = useState(0);
  const [descJuros, setDescJuros] = useState(0);
  const [periodicidade, setPeriodicidade] = useState('mensal'); // mensal | quinzenal | semanal | custom
  const [minParcela, setMinParcela] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!open) {
      setProposta(null); setTipo('avista'); setDescontoPct(10);
      setParcelas(3); setPrimDias(7); setIntervalo(30);
      setValidadeDias(7); setObservacao(''); setLoading(false);
      setEntradaTipo('none'); setEntradaValor(0); setEntradaVencimentoDias('');
      setDescPrincipal(0); setDescMulta(0); setDescJuros(0);
      setPeriodicidade('mensal'); setMinParcela(0); setShowAdvanced(false);
    }
  }, [open]);

  // Sync periodicidade → intervalo dias
  useEffect(() => {
    if (periodicidade === 'mensal') setIntervalo(30);
    else if (periodicidade === 'quinzenal') setIntervalo(15);
    else if (periodicidade === 'semanal') setIntervalo(7);
  }, [periodicidade]);

  // ─── Cálculo em tempo real ─────────────────────────────────────────────
  const sim = useMemo(() => {
    const totais = devedor?.totais || {};
    const original = Number(totais.valor_original_total || 0);
    const atualizado = Number(totais.valor_atualizado_total || 0);
    const multa = Number(totais.multa_total || 0);
    const juros = Number(totais.juros_total || 0);
    const principal = Math.max(atualizado - multa - juros, 0);

    // Descontos separados por componente
    const descPrincipalV = (principal * (Number(descPrincipal) || 0)) / 100;
    const descMultaV = (multa * (Number(descMulta) || 0)) / 100;
    const descJurosV = (juros * (Number(descJuros) || 0)) / 100;
    let descontoTotal = descPrincipalV + descMultaV + descJurosV;

    // Desconto simples (compatível com modal atual): aplica só se todos os separados forem 0
    if (descontoTotal === 0 && Number(descontoPct) > 0) {
      descontoTotal = (atualizado * Number(descontoPct)) / 100;
    }
    const totalNegociado = Math.max(atualizado - descontoTotal, 0);

    // Entrada
    let entrada = 0;
    if (entradaTipo === 'pct') entrada = (totalNegociado * (Number(entradaValor) || 0)) / 100;
    else if (entradaTipo === 'valor') entrada = Math.min(Number(entradaValor) || 0, totalNegociado);
    entrada = Math.round(entrada * 100) / 100;

    const saldo = Math.max(totalNegociado - entrada, 0);

    // Parcelamento
    const qtdParcelas = tipo === 'parcelado' ? Math.max(1, Number(parcelas) || 1) : 1;
    let efetivasParcelas = qtdParcelas;
    let valorParcela = tipo === 'parcelado' ? saldo / qtdParcelas : saldo;
    if (tipo === 'parcelado' && Number(minParcela) > 0 && valorParcela < Number(minParcela)) {
      efetivasParcelas = Math.max(1, Math.floor(saldo / Number(minParcela)));
      valorParcela = saldo / efetivasParcelas;
    }
    valorParcela = Math.round(valorParcela * 100) / 100;

    // Cronograma
    const entradaDias = (entradaVencimentoDias === '' || entradaVencimentoDias === null)
      ? Number(primDias)
      : Math.max(0, Number(entradaVencimentoDias) || 0);
    const cronograma = [];
    if (entrada > 0) cronograma.push({ n: 0, tipo: 'Entrada', dias: entradaDias, valor: entrada });
    for (let i = 1; i <= efetivasParcelas; i++) {
      const dias = Number(primDias) + (i - 1) * Number(intervalo);
      cronograma.push({ n: i, tipo: `Parcela ${i}/${efetivasParcelas}`, dias, valor: valorParcela });
    }

    const economia = atualizado - totalNegociado;
    const economiaPct = atualizado > 0 ? (economia / atualizado) * 100 : 0;

    return {
      original, atualizado, multa, juros, principal,
      descPrincipalV, descMultaV, descJurosV, descontoTotal,
      totalNegociado, entrada, entradaDias, saldo,
      qtdParcelas: efetivasParcelas, valorParcela, cronograma,
      economia, economiaPct,
      primeiraParcelaDias: Number(primDias),
      ultimaParcelaDias: Number(primDias) + (efetivasParcelas - 1) * Number(intervalo),
    };
  }, [devedor, descontoPct, tipo, parcelas, primDias, intervalo,
      entradaTipo, entradaValor, entradaVencimentoDias, descPrincipal, descMulta, descJuros, minParcela]);

  const gerar = async () => {
    if (!devedor?.devedor_key) return;
    setLoading(true);
    try {
      const usouSeparados = (Number(descPrincipal) + Number(descMulta) + Number(descJuros)) > 0;
      const descontoEfetivoPct = usouSeparados
        ? (sim.atualizado > 0 ? (sim.descontoTotal / sim.atualizado) * 100 : 0)
        : Number(descontoPct || 0);
      const payload = {
        tipo,
        desconto_pct: Math.round(descontoEfetivoPct * 100) / 100,
        parcelas: tipo === 'parcelado' ? sim.qtdParcelas : 1,
        primeira_parcela_dias: Number(primDias || 7),
        intervalo_dias: Number(intervalo || 30),
        validade_dias: Number(validadeDias || 7),
        observacao: observacao || null,
        // V4.31 — Campos de entrada (backend agora processa e persiste)
        entrada_tipo: entradaTipo,
        entrada_valor: Number(entradaValor) || 0,
        entrada_vencimento_dias: sim.entradaDias,
        // V4.30 — Simulação avançada (metadata p/ auditoria)
        simulacao_avancada: {
          entrada_tipo: entradaTipo, entrada_valor: Number(entradaValor) || 0,
          entrada_calculada: sim.entrada,
          desconto_principal_pct: Number(descPrincipal) || 0,
          desconto_multa_pct: Number(descMulta) || 0,
          desconto_juros_pct: Number(descJuros) || 0,
          periodicidade, valor_min_parcela: Number(minParcela) || 0,
          totais: {
            original: sim.original, atualizado: sim.atualizado,
            multa: sim.multa, juros: sim.juros,
            desconto_total: sim.descontoTotal, total_negociado: sim.totalNegociado,
            saldo: sim.saldo, economia: sim.economia, economia_pct: sim.economiaPct,
          },
        },
      };
      const { data } = await api.post(`/dnapay/cobranca/devedores/${encodeURIComponent(devedor.devedor_key)}/proposta`, payload);
      setProposta(data);
      toast.success('Proposta gerada');
    } catch (e) {
      toast.error('Falha ao gerar proposta', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  const baixarPdf = async () => {
    if (!proposta?.id) return;
    try {
      const r = await api.get(`/dnapay/cobranca/propostas/${proposta.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `proposta_${proposta.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Proposta PDF baixada');
    } catch { toast.error('Falha ao baixar PDF'); }
  };

  const enviarWhatsApp = async () => {
    if (!proposta) return;
    if (!devedor?.responsavel_whatsapp) { toast.error('Sem WhatsApp'); return; }
    const venc = proposta.parcelas?.[0]?.vencimento;
    const msg = proposta.tipo === 'avista'
      ? `Olá ${proposta.responsavel_nome || ''}, segue *Proposta de Acordo* para ${proposta.aluno_nome || ''}:\n\n` +
        `• Valor atualizado: ${fmtBRL(proposta.valor_atualizado)}\n` +
        `• Desconto à vista (${proposta.desconto_pct}%): -${fmtBRL(proposta.desconto_valor)}\n` +
        `• *Valor proposto: ${fmtBRL(proposta.valor_final)}*\n` +
        `• Vencimento: ${fmtDateBR(venc)}\n\n` +
        `Validade da proposta: ${fmtDateBR(proposta.validade)}.\n` +
        `Proposta nº ${proposta.id.slice(0, 8).toUpperCase()}.`
      : `Olá ${proposta.responsavel_nome || ''}, segue *Proposta de Acordo Parcelado* para ${proposta.aluno_nome || ''}:\n\n` +
        `• Valor atualizado: ${fmtBRL(proposta.valor_atualizado)}\n` +
        `• *Total: ${fmtBRL(proposta.valor_final)}* em ${proposta.qtd_parcelas}x\n\n` +
        proposta.parcelas.map((p) => `  ${String(p.n).padStart(2, '0')}/${String(proposta.qtd_parcelas).padStart(2, '0')} · ${fmtDateBR(p.vencimento)} · ${fmtBRL(p.valor)}`).join('\n') +
        `\n\nValidade: ${fmtDateBR(proposta.validade)}.\nProposta nº ${proposta.id.slice(0, 8).toUpperCase()}.`;
    const url = buildWhatsappLink(devedor.responsavel_whatsapp, msg);
    window.open(url, '_blank', 'noopener,noreferrer');
    try {
      const t0 = devedor.debitos?.[0];
      if (t0) await api.post('/dnapay/cobranca/log-manual-action', {
        titulo_id: t0.titulo_id, action: 'whatsapp_manual_opened',
        canal: 'whatsapp', corpo: msg, destino: devedor.responsavel_whatsapp,
        template_nome: `proposta_${proposta.tipo}`,
      });
    } catch (e) { /* ignore */ }
    toast.success('WhatsApp aberto com proposta');
  };

  const aceitar = async () => {
    if (!proposta?.id) return;
    if (!window.confirm(`Confirmar ACEITE da proposta de ${fmtBRL(proposta.valor_final)}?\n\nIsso suspenderá a régua para ${(proposta.titulos_ids || []).length} título(s) (status → renegociado).`)) return;
    try {
      const { data } = await api.post(`/dnapay/cobranca/propostas/${proposta.id}/aceitar`);
      toast.success(`Acordo registrado · ${data.titulos_atualizados} título(s) atualizado(s)`);
      onAccepted && onAccepted();
    } catch (e) {
      toast.error('Falha', { description: e?.response?.data?.detail });
    }
  };

  if (!devedor) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="proposta-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" /> Simulação de Proposta de Acordo
          </DialogTitle>
          <DialogDescription>
            {devedor.responsavel_nome} · {devedor.responsavel_cpf} · Atualizado: <span className="font-semibold text-rose-700">{fmtBRL(devedor.totais?.valor_atualizado_total)}</span>
          </DialogDescription>
        </DialogHeader>

        {!proposta && (
          <>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-zinc-500 mb-1.5 block">Modalidade</Label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger className="rounded-lg" data-testid="proposta-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avista">À vista (com desconto)</SelectItem>
                    <SelectItem value="parcelado">Parcelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Desconto geral (%)</Label>
                <Input type="number" step="0.1" value={descontoPct} onChange={(e) => setDescontoPct(e.target.value)} className="rounded-lg" data-testid="proposta-desconto" />
                <p className="text-[10px] text-zinc-400 mt-1">Ignorado se usar descontos separados abaixo</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Validade da proposta (dias)</Label>
                <Input type="number" value={validadeDias} onChange={(e) => setValidadeDias(e.target.value)} className="rounded-lg" />
              </div>
              {tipo === 'parcelado' && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1.5 block">Nº de parcelas</Label>
                    <Input type="number" min={1} max={36} value={parcelas} onChange={(e) => setParcelas(e.target.value)} className="rounded-lg" data-testid="proposta-parcelas" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1.5 block">Primeira parcela em (dias)</Label>
                    <Input type="number" value={primDias} onChange={(e) => setPrimDias(e.target.value)} className="rounded-lg" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1.5 block">Periodicidade</Label>
                    <Select value={periodicidade} onValueChange={setPeriodicidade}>
                      <SelectTrigger className="rounded-lg" data-testid="proposta-periodicidade"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensal">Mensal (30 dias)</SelectItem>
                        <SelectItem value="quinzenal">Quinzenal (15 dias)</SelectItem>
                        <SelectItem value="semanal">Semanal (7 dias)</SelectItem>
                        <SelectItem value="custom">Personalizada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {periodicidade === 'custom' && (
                    <div>
                      <Label className="text-xs text-zinc-500 mb-1.5 block">Intervalo (dias)</Label>
                      <Input type="number" value={intervalo} onChange={(e) => setIntervalo(e.target.value)} className="rounded-lg" data-testid="proposta-intervalo" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1.5 block">Valor mínimo por parcela (R$)</Label>
                    <Input type="number" step="0.01" min={0} value={minParcela} onChange={(e) => setMinParcela(e.target.value)} className="rounded-lg" data-testid="proposta-min-parcela" placeholder="0 = sem limite" />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <Label className="text-xs text-zinc-500 mb-1.5 block">Observação (opcional)</Label>
                <Textarea rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} className="rounded-lg" />
              </div>
            </div>

            {/* V4.30 — Bloco expansível: Simulação avançada */}
            <div className="mt-4 border border-indigo-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 transition"
                data-testid="proposta-advanced-toggle"
              >
                <span className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Simulação avançada (entrada, descontos separados)
                </span>
                <ChevronRight className={`h-4 w-4 text-indigo-700 transition ${showAdvanced ? 'rotate-90' : ''}`} />
              </button>
              {showAdvanced && (
                <div className="p-4 space-y-3 bg-white">
                  {/* Entrada */}
                  <div>
                    <Label className="text-xs text-zinc-600 font-semibold mb-2 block">Entrada</Label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {[['none','Sem entrada'],['pct','Percentual'],['valor','Valor fixo']].map(([k,l]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => { setEntradaTipo(k); if (k==='none') setEntradaValor(0); }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${entradaTipo === k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50'}`}
                          data-testid={`proposta-entrada-${k}`}
                        >{l}</button>
                      ))}
                    </div>
                    {entradaTipo !== 'none' && (
                      <>
                        <Input type="number" step="0.01" min={0} value={entradaValor} onChange={(e) => setEntradaValor(e.target.value)} placeholder={entradaTipo === 'pct' ? '10' : '5000.00'} className="rounded-lg" data-testid="proposta-entrada-valor" />
                        <div className="mt-2">
                          <Label className="text-xs text-zinc-600 mb-1 block flex items-center justify-between">
                            <span>Vencimento da entrada (dias a partir de hoje)</span>
                            {entradaVencimentoDias === '' && (
                              <span className="text-[10px] text-indigo-600 font-normal">padrão: {Number(primDias) || 0}d (mesma da 1ª parcela)</span>
                            )}
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="1"
                              min={0}
                              value={entradaVencimentoDias}
                              onChange={(e) => setEntradaVencimentoDias(e.target.value)}
                              placeholder={`${Number(primDias) || 0}`}
                              className="rounded-lg"
                              data-testid="proposta-entrada-vencimento-dias"
                            />
                            {entradaVencimentoDias !== '' && (
                              <button
                                type="button"
                                onClick={() => setEntradaVencimentoDias('')}
                                className="text-[11px] text-zinc-500 hover:text-indigo-700 underline whitespace-nowrap"
                                data-testid="proposta-entrada-venc-reset"
                              >Usar padrão</button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Descontos separados */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-zinc-600 mb-1 block">Desconto Principal (%)</Label>
                      <Input type="number" step="0.1" min={0} max={100} value={descPrincipal} onChange={(e) => setDescPrincipal(e.target.value)} className="rounded-lg" data-testid="proposta-desc-principal" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-600 mb-1 block">Desconto Multa (%)</Label>
                      <Input type="number" step="0.1" min={0} max={100} value={descMulta} onChange={(e) => setDescMulta(e.target.value)} className="rounded-lg" data-testid="proposta-desc-multa" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-600 mb-1 block">Desconto Juros (%)</Label>
                      <Input type="number" step="0.1" min={0} max={100} value={descJuros} onChange={(e) => setDescJuros(e.target.value)} className="rounded-lg" data-testid="proposta-desc-juros" />
                    </div>
                  </div>
                  <p className="text-[11px] text-indigo-700 bg-indigo-50 rounded px-2 py-1">
                    Descontos separados <b>substituem</b> o desconto geral acima quando qualquer um for &gt; 0.
                  </p>
                </div>
              )}
            </div>

            {/* V4.30 — Resumo em tempo real */}
            <div className="mt-4 bg-gradient-to-br from-zinc-50 to-slate-50 border border-zinc-200 rounded-xl p-4" data-testid="proposta-resumo-live">
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                <Activity className="h-3.5 w-3.5" /> Resumo (tempo real)
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div><div className="text-[10px] text-zinc-500 uppercase">Original</div><div className="font-semibold tabular-nums">{fmtBRL(sim.original)}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Atualizado</div><div className="font-semibold tabular-nums">{fmtBRL(sim.atualizado)}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Multa</div><div className="font-semibold text-amber-700 tabular-nums">{fmtBRL(sim.multa)}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Juros</div><div className="font-semibold text-orange-700 tabular-nums">{fmtBRL(sim.juros)}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Desconto total</div><div className="font-semibold text-rose-700 tabular-nums">-{fmtBRL(sim.descontoTotal)}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Entrada</div><div className="font-semibold text-indigo-700 tabular-nums">{fmtBRL(sim.entrada)}{sim.entrada > 0 && <span className="text-[10px] text-zinc-500 font-normal ml-1">em {sim.entradaDias}d</span>}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Saldo</div><div className="font-semibold tabular-nums">{fmtBRL(sim.saldo)}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase">Total negociado</div><div className="font-bold text-emerald-700 tabular-nums">{fmtBRL(sim.totalNegociado)}</div></div>
                {tipo === 'parcelado' && (
                  <>
                    <div className="col-span-2"><div className="text-[10px] text-zinc-500 uppercase">Parcelas</div><div className="font-semibold tabular-nums">{sim.qtdParcelas}x {fmtBRL(sim.valorParcela)}</div></div>
                    <div><div className="text-[10px] text-zinc-500 uppercase">1ª parcela</div><div className="font-semibold tabular-nums text-xs">em {sim.primeiraParcelaDias}d</div></div>
                    <div><div className="text-[10px] text-zinc-500 uppercase">Última</div><div className="font-semibold tabular-nums text-xs">em {sim.ultimaParcelaDias}d</div></div>
                  </>
                )}
                <div className="col-span-2 md:col-span-4 pt-2 mt-1 border-t border-zinc-200 flex justify-between items-baseline">
                  <span className="text-xs text-zinc-500">Economia total</span>
                  <span className="text-base font-bold text-emerald-700 tabular-nums">{fmtBRL(sim.economia)} <span className="text-xs text-emerald-600">({sim.economiaPct.toFixed(1)}%)</span></span>
                </div>
              </div>
              {tipo === 'parcelado' && sim.cronograma.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-indigo-700 cursor-pointer hover:text-indigo-900">Ver cronograma ({sim.cronograma.length} itens)</summary>
                  <div className="mt-2 max-h-40 overflow-auto text-xs bg-white rounded border border-zinc-200 p-2">
                    {sim.cronograma.map((p) => (
                      <div key={`${p.n}-${p.tipo}`} className="flex justify-between py-0.5 border-b border-zinc-100 last:border-0">
                        <span className="text-zinc-700">{p.tipo} · em {p.dias}d</span>
                        <span className="font-mono font-semibold">{fmtBRL(p.valor)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </>
        )}

        {proposta && (
          <div className="mt-2 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiPill label="Original" value={fmtBRL(proposta.valor_original)} color="zinc" />
              <KpiPill label="Atualizado" value={fmtBRL(proposta.valor_atualizado)} color="zinc" />
              <KpiPill label="Desconto" value={fmtBRL(proposta.desconto_valor || 0)} color="amber" />
              <KpiPill label="Proposta" value={fmtBRL(proposta.valor_final)} color="emerald" />
              {Number(proposta.entrada_valor || 0) > 0 && (
                <>
                  <KpiPill label="Entrada" value={fmtBRL(proposta.entrada_valor)} color="indigo" />
                  <KpiPill label="Saldo parcelado" value={fmtBRL(proposta.saldo_parcelado)} color="zinc" />
                </>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="text-xs font-medium uppercase tracking-wider text-amber-700 mb-2">
                {proposta.tipo === 'avista'
                  ? 'Pagamento À Vista'
                  : (Number(proposta.entrada_valor || 0) > 0
                      ? `Entrada + ${proposta.qtd_parcelas} parcelas`
                      : `${proposta.qtd_parcelas} parcelas`)}
              </div>
              <div className="space-y-1">
                {proposta.parcelas.map((p) => (
                  <div key={`${p.tipo || 'p'}-${p.n}-${p.vencimento}`} className={`flex justify-between text-sm tabular-nums ${p.tipo === 'entrada' ? 'bg-indigo-50 border border-indigo-200 rounded px-2 py-1 -mx-1 my-1' : ''}`} data-testid={p.tipo === 'entrada' ? 'proposta-linha-entrada' : `proposta-parcela-${p.n}`}>
                    <span className={p.tipo === 'entrada' ? 'text-indigo-900 font-semibold' : 'text-amber-900'}>
                      {p.tipo === 'entrada'
                        ? `Entrada · ${fmtDateBR(p.vencimento)}`
                        : `Parcela ${String(p.n).padStart(2, '0')}/${String(proposta.qtd_parcelas).padStart(2, '0')} · ${fmtDateBR(p.vencimento)}`}
                    </span>
                    <span className={`font-semibold ${p.tipo === 'entrada' ? 'text-indigo-900' : 'text-amber-900'}`}>{fmtBRL(p.valor)}</span>
                  </div>
                ))}
              </div>
              <Separator className="my-3 bg-amber-200" />
              <div className="flex justify-between text-sm">
                <span className="text-amber-900">Validade da proposta</span>
                <span className="font-semibold text-amber-900">{fmtDateBR(proposta.validade)}</span>
              </div>
            </div>

            <div className="text-[11px] text-zinc-400 font-mono">
              Proposta nº <span className="uppercase">{proposta.id.slice(0, 8)}</span> · hash {proposta.hash}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          {!proposta && (
            <Button onClick={gerar} disabled={loading} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="proposta-gerar-btn">
              <Sparkles className="h-4 w-4 mr-2" />
              {loading ? 'Calculando...' : 'Gerar simulação'}
            </Button>
          )}
          {proposta && (
            <>
              <Button variant="outline" onClick={baixarPdf} className="rounded-full" data-testid="proposta-pdf-btn">
                <FileText className="h-4 w-4 mr-2" /> Baixar PDF
              </Button>
              <Button onClick={enviarWhatsApp} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="proposta-wa-btn">
                <MessageSquare className="h-4 w-4 mr-2" /> Enviar via WhatsApp
              </Button>
              <Button onClick={aceitar} className="rounded-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="proposta-aceitar-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Marcar como aceita
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Tab Histórico de Importações ──────────────────────────────────────────
function ImportacoesTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [reverter, setReverter] = useState(null); // {batch_id, filename, total}
  const [verDebitos, setVerDebitos] = useState(null); // batch_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get('/dnapay/cobranca/importacoes');
      setData(r);
    } catch (e) {
      toast.error('Erro ao listar importações');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusBadge = (s) => {
    const map = {
      ativa: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
      revertida: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
      parcialmente_revertida: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
      com_movimentacao: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
      vazia: 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200',
    };
    return map[s] || map.vazia;
  };

  return (
    <Card className="border-zinc-200/70 rounded-2xl shadow-none">
      <CardHeader className="border-b border-zinc-100 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base font-semibold">Histórico de Importações Excel</CardTitle>
          <p className="text-xs text-zinc-500 mt-1">Lotes de cobrança extrajudicial importados. Reverter remove os débitos do lote da listagem ativa (lógico — auditoria mantida).</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-full border-zinc-300" data-testid="importacoes-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80">
              <TableHead>Arquivo</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Importado por</TableHead>
              <TableHead className="text-center">Total</TableHead>
              <TableHead className="text-center">Ativos</TableHead>
              <TableHead className="text-center">Rev.</TableHead>
              <TableHead className="text-center">Mov.</TableHead>
              <TableHead className="text-center">Ex-alunos</TableHead>
              <TableHead className="text-right">Atualizado</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-44">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && !loading && (
              <TableRow><TableCell colSpan={11} className="text-center py-12 text-zinc-400">Nenhuma importação registrada ainda.</TableCell></TableRow>
            )}
            {data.items.map((it) => (
              <TableRow key={it.import_batch_id} data-testid={`importacao-row-${it.import_batch_id}`}>
                <TableCell>
                  <div className="text-sm font-medium text-zinc-900">{it.filename || '—'}</div>
                  <div className="text-[10px] text-zinc-400 font-mono">{it.import_batch_id.slice(0, 16)}</div>
                </TableCell>
                <TableCell className="text-xs tabular-nums">{it.imported_at?.slice(0, 19).replace('T', ' ') || '—'}</TableCell>
                <TableCell className="text-xs">{it.imported_by || '—'}</TableCell>
                <TableCell className="text-center tabular-nums">{it.total}</TableCell>
                <TableCell className="text-center tabular-nums text-emerald-700">{it.ativos}</TableCell>
                <TableCell className="text-center tabular-nums text-rose-600">{it.revertidos}</TableCell>
                <TableCell className="text-center tabular-nums text-amber-600">{it.com_movimentacao}</TableCell>
                <TableCell className="text-center tabular-nums text-violet-700">{it.ex_alunos_unicos}</TableCell>
                <TableCell className="text-right tabular-nums text-sm font-medium text-rose-700">{fmtBRL(it.valor_atualizado_total)}</TableCell>
                <TableCell>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${statusBadge(it.status)}`}>{it.status}</span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setVerDebitos(it)}
                      data-testid={`importacao-ver-${it.import_batch_id}`}
                      title="Ver débitos"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReverter(it)}
                      disabled={!it.pode_reverter}
                      className="text-rose-700 hover:bg-rose-50 disabled:opacity-30"
                      data-testid={`importacao-reverter-${it.import_batch_id}`}
                      title="Reverter lote"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <ReverterImportacaoDialog
        target={reverter}
        onClose={() => setReverter(null)}
        onReverted={() => { setReverter(null); load(); }}
      />

      <VerDebitosImportacaoSheet
        target={verDebitos}
        onClose={() => setVerDebitos(null)}
      />
    </Card>
  );
}

function VerDebitosImportacaoSheet({ target, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) { setData(null); return; }
    setLoading(true);
    api.get(`/dnapay/cobranca/importacoes/${encodeURIComponent(target.import_batch_id)}/debitos`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Erro ao carregar débitos'))
      .finally(() => setLoading(false));
  }, [target]);

  return (
    <Sheet open={!!target} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-3xl overflow-y-auto" data-testid="ver-debitos-sheet">
        <SheetHeader>
          <SheetTitle>Débitos da Importação</SheetTitle>
          <SheetDescription>
            {target && (
              <>
                <strong>{target.filename}</strong>
                <div className="text-xs mt-1">
                  {target.total} débito(s) · {target.ex_alunos_unicos} ex-aluno(s) único(s) · Atualizado: {fmtBRL(target.valor_atualizado_total)}
                </div>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {loading && <div className="text-center text-zinc-400 py-12">Carregando...</div>}
        {data && (
          <div className="mt-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80">
                  <TableHead>Aluno</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead>Comp.</TableHead>
                  <TableHead>Venc.</TableHead>
                  <TableHead className="text-right">Original</TableHead>
                  <TableHead className="text-right">Atual.</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((d) => (
                  <TableRow key={d.titulo_id}>
                    <TableCell className="text-sm">{d.aluno_nome || '—'}</TableCell>
                    <TableCell className="text-xs">{d.responsavel_nome || '—'}</TableCell>
                    <TableCell className="text-xs">{d.competencia || '—'}</TableCell>
                    <TableCell className="text-xs tabular-nums">{fmtDateBR(d.vencimento)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{fmtBRL(d.valor_original)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-rose-700 font-medium">{fmtBRL(d.valor_atualizado)}</TableCell>
                    <TableCell><span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">{d.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ReverterImportacaoDialog({ target, onClose, onReverted }) {
  const [confirmacao, setConfirmacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => { if (!target) { setConfirmacao(''); setReport(null); setLoading(false); } }, [target]);

  const submit = async () => {
    if (confirmacao !== 'REVERTER') {
      toast.error('Digite exatamente REVERTER para confirmar'); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/dnapay/cobranca/importacoes/${target.import_batch_id}/reverter`, { confirmacao });
      setReport(data);
      toast.success(`${data.totais.revertidos} débito(s) revertido(s)`);
    } catch (e) {
      toast.error('Falha ao reverter', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reverter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-4 w-4" /> Reverter Importação
          </DialogTitle>
          <DialogDescription>
            {target && (
              <>
                <div className="mt-2"><strong>{target.filename}</strong></div>
                <div className="text-xs mt-1">
                  {target.total} débito(s) · Valor total {fmtBRL(target.valor_total)} · Importado em {target.imported_at?.slice(0, 19).replace('T', ' ')}
                </div>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!report && (
          <>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900 mt-2">
              <strong>Atenção:</strong> esta ação irá reverter todos os débitos desta importação.
              Os registros serão marcados como <code>status=revertido</code> e sairão da listagem ativa.
              A auditoria permanece registrada.
            </div>
            <div className="mt-3">
              <Label className="text-xs text-zinc-500 mb-1.5 block">Digite <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-rose-700 font-mono">REVERTER</code> para confirmar:</Label>
              <Input
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                placeholder="REVERTER"
                className="rounded-lg font-mono"
                data-testid="reverter-input"
                autoFocus
              />
            </div>
          </>
        )}

        {report && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <KpiPill label="Encontrados" value={report.totais.encontrados} color="zinc" />
            <KpiPill label="Revertidos" value={report.totais.revertidos} color="rose" />
            <KpiPill label="Bloqueados" value={report.totais.bloqueados} color="amber" />
            <KpiPill label="Já revertidos" value={report.totais.ja_revertidos} color="zinc" />
          </div>
        )}

        <DialogFooter className="mt-4">
          {!report ? (
            <>
              <Button variant="ghost" onClick={onClose} disabled={loading}>Cancelar</Button>
              <Button
                onClick={submit}
                disabled={loading || confirmacao !== 'REVERTER'}
                className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="reverter-confirm-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> {loading ? 'Revertendo...' : 'Confirmar reversão'}
              </Button>
            </>
          ) : (
            <Button onClick={onReverted} className="rounded-full bg-zinc-900 hover:bg-zinc-800">Fechar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

